/**
 * The operator console. M8, F-80 to F-84.
 *
 * ---
 *
 * **This whole directory is English-only and deliberately does not go through
 * `t()`.** It is the one exception to the i18n rule that governs the rest of this
 * app (D-10, spec.md "Multilingual from the first commit"), and it is an exception
 * rather than an oversight.
 *
 * Three reasons. It has exactly one user, and he reads English. Most of its text is
 * not prose but field names and identifiers — `stability`, `difficulty`, `lapses`,
 * `nodeId`, `gsi2sk`, `relearning` — which are the names in the code and in the
 * DynamoDB rows, and translating them would mean an operator reading a Spanish word
 * on screen and grepping for an English one. And the copy here changes with every
 * pass over the data model, so a second dictionary would be permanently half-stale,
 * which is worse than no dictionary: a missing Spanish key is a compile error, and
 * paying that price for a screen nobody reads in Spanish would slow down the tool
 * whose entire job is being quick to change.
 *
 * The learner app keeps the rule absolutely. Nothing under `src/admin/` may import
 * `useI18n`, and no copy key may be added for a console string.
 *
 * ---
 *
 * Read-only apart from four named writes — re-run each of the three passes, and force
 * a sweep — each behind a confirmation that states its price first (D-19, F-83).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import NotFound from '../NotFound';
import SignIn from '../SignIn';
import { operatorAccess, type Access } from './api';
import Learners from './Learners';
import Learner from './Learner';
import Objective from './Objective';
import Items from './Items';
import './console.css';

/*
 * Whether the console exists, for this session.
 *
 * `undefined` is "not asked yet" and is emphatically not "no" — the same distinction
 * the learner app draws for the session cookie, for the same reason: guessing paints
 * the wrong screen and then corrects itself.
 *
 * The difference is what it paints while it does not know: **nothing at all.** No
 * spinner, no "checking access", no shell of a console that a non-operator watches
 * collapse into a 404. Anything rendered here is a statement that there is something
 * at this URL to be authorised for, which is exactly what D-20 exists to withhold.
 */
export default function Console() {
  const [access, setAccess] = useState<Access>();

  const ask = useCallback(() => {
    void operatorAccess().then(setAccess);
  }, []);

  useEffect(() => {
    // Asked once. Asked again only after a sign-in, which is the one thing that can
    // change the answer without a reload.
    ask();
  }, [ask]);

  /*
   * The console is wide; the learner app is a 46rem column. Widening happens by a
   * class on `body`, and *only once the answer is yes*.
   *
   * That ordering is the point. If the class went on before `/api/admin/me` answered,
   * a non-operator's 404 would render inside a header stretched to full width — a
   * page visibly different from the 404 at `/asdf`, and therefore a page that admits
   * `/admin` is real. Same reason nothing renders while `access` is `undefined`.
   */
  useEffect(() => {
    if (access !== 'operator') return;
    document.body.classList.add('is-console');
    return () => document.body.classList.remove('is-console');
  }, [access]);

  // Not asked yet. One request's worth of blank, and no more.
  if (access === undefined) return null;

  /*
   * Not an operator, or the API is not answering — one indistinguishable answer.
   *
   * The *same component* the router renders for a wrong URL, not a copy of it: see
   * `NotFound.tsx`. A copy would drift by a word eventually, and the drift would be
   * the tell.
   */
  if (access === 'absent') return <NotFound />;

  /*
   * Signed out, which is the one refusal answered with something useful.
   *
   * The ordinary sign-in screen, and then the question is asked again — no reload, no
   * second navigation. This does admit to a signed-out visitor that `/admin` routes
   * somewhere, and that is accepted: `/api/admin/me` answers 401 to a signed-out
   * request no matter what this screen draws, so the disclosure has already happened
   * at the endpoint. What it buys is that an operator whose session lapsed signs in
   * and lands where he was, rather than reading a 404 about his own console.
   */
  if (access === 'signed_out') return <SignIn onSignedIn={ask} />;

  return (
    <div className="console">
      <nav className="console-bar" aria-label="Console">
        <Link to="/admin" className="console-home">
          console
        </Link>
        <span className="console-note">
          read-only, apart from re-runs and sweeps · every read is logged
        </span>
      </nav>

      <Routes>
        <Route index element={<Learners />} />
        <Route path="learner/:userId" element={<Learner />} />
        <Route path="objective/:objectiveId" element={<Objective />} />
        <Route path="objective/:objectiveId/items" element={<Items />} />
        {/* A wrong path under /admin is as wrong as any other. An operator who
            mistypes gets the app's 404 rather than an empty console frame. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}
